/**
 * Copyright Highway9 Networks Inc.
 */
import React from "react";
import { Button, FormControlLabel, Grid, Input, Radio, RadioGroup, Tooltip, Typography } from "@h9/ui-lib";
import { trimSpaces } from "../../components/Utils";
import { text } from "../../components/TextResources";
import { CpiDetails, UserInfo } from "~/types/user";

interface CPIDetailsProps {
  user?: UserInfo;
  cpiDetails: CpiDetails;
  clearCPIDetails?: () => void;
}

export function CPIDetails(props: CPIDetailsProps) {
  const [cpiId, setCpiId] = React.useState<string>("");
  const [cpiName, setCpiName] = React.useState<string>("");
  const [cpiPassword, setCpiPassword] = React.useState<string>("");
  const [filename, setCpiFilename] = React.useState<string>("");
  const [saveCpiDetails, setSaveDetails] = React.useState<string>("always");

  React.useEffect(() => {
    setCpiId(props.user?.cpiDetails?.id || "");
    setCpiName(props.user?.cpiDetails?.name || "");
    setCpiPassword(props.user?.cpiDetails?.password || "");
    setCpiFilename(props.user?.cpiDetails?.keyfile?.name || "");
    setSaveDetails(props.user?.cpiDetails?.savePolicy || "always");

    return () => {
      // console.log("CPIDetails unmounted")
      reset();
    };
  }, [props.user]);

  function reset() {
    setCpiId("");
    setCpiName("");
    setCpiPassword("");
    setCpiFilename("");
  }

  function handleCpiFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const cpiKeyFilename = file.name;
    setCpiFilename(cpiKeyFilename);
    // console.log("Filename ", cpiKeyFilename)

    event.preventDefault();
    const reader = new FileReader();
    reader.onload = async (ev: ProgressEvent<FileReader> | any) => {
      const textContent = ev.target.result as string;
      const cpikeyfile = {
        name: cpiKeyFilename,
        content: btoa(textContent),
      };
      props.cpiDetails.keyfile = cpikeyfile;
    };

    reader.readAsBinaryString(file);
    if (!props.cpiDetails.keyfile) props.cpiDetails.keyfile = { name: cpiKeyFilename };
    else props.cpiDetails.keyfile.name = cpiKeyFilename;
  }

  function handleCpiNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    const name = event.target.value;
    setCpiName(name);
    props.cpiDetails.name = name;
  }

  function handleCpiIdChange(event: React.ChangeEvent<HTMLInputElement>) {
    const id = trimSpaces(event.target.value);
    setCpiId(id);
    props.cpiDetails.id = id;
  }

  function handleCpiPasswordChange(event: React.ChangeEvent<HTMLInputElement>) {
    const password = trimSpaces(event.target.value);
    setCpiPassword(password);
    props.cpiDetails.password = password;
  }

  function handleSaveDetailsChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSaveDetails(event.target.value);
    props.cpiDetails.savePolicy = event.target.value;
  }

  function handleClearSettings(_event?: React.MouseEvent) {
    setCpiId("");
    setCpiName("");
    setCpiPassword("");
    setCpiFilename("");
    props.clearCPIDetails?.();
  }

  return (
    <Grid>
      <Grid xs={12}>
        <Input
          variant="standard"
          label="Name"
          type="text"
          id="name"
          required
          value={cpiName || ""}
          onChange={handleCpiNameChange}
          fullWidth
        ></Input>{" "}
        <br />
        <br />
        <Input
          autoComplete="cpiID"
          variant="standard"
          label="ID"
          type="text"
          id="id"
          required
          value={cpiId || ""}
          onChange={handleCpiIdChange}
          fullWidth
        ></Input>{" "}
        <br />
        <br />
        <Input
          variant="standard"
          label="Password"
          id="cpi-password"
          required
          value={cpiPassword || ""}
          onChange={handleCpiPasswordChange}
          fullWidth
          type={"password"}
          autoComplete="new-password"
          sx={{
            paddingRight: '16px',
          }}
        />
        <br />
        <br />
        <div id="upload-box">
          <Typography>CPI Key: {filename}</Typography>
          <input type="file" id="file-selector" accept=".p12" onChange={handleCpiFileChange} />
          <br />
          <br />
        </div>
        <Typography>Save CPI details for future use:</Typography>
        <RadioGroup row value={saveCpiDetails} onChange={handleSaveDetailsChange}>
          <Tooltip title={"CPI Details will be saved only for current session"} arrow={true}>
            <FormControlLabel
              value="session"
              label="Save for current session only"
              control={<Radio style={{ color: "#026950" }} sx={{p: 1}}/>}
            />
          </Tooltip>
          <Tooltip title={"CPI Details will be saved for future use"} arrow={true}>
            <FormControlLabel value="always" label="Always" control={<Radio style={{ color: "#026950" }} sx={{p: 1}}/>} />
          </Tooltip>
        </RadioGroup>
        <br />
        <Button variant="contained" autoFocus onClick={handleClearSettings} color="primary" sx={{textTransform: 'uppercase'}}>
          {text.clearSettings}
        </Button>
      </Grid>
    </Grid>
  );
}
